/** 历史首板 / 记录库共用：筹码分档与斜率过滤配置 */

import { passesMa250Filter } from '@/views/GapUpAnalysis/assets/js/ma250Filter'

export const FILTER_CONFIG_KEY = 'historyFirstBoardChipRanges'
export const RANGE_FIELDS = [
  { key: 'short', label: '短期' },
  { key: 'mid', label: '中期' },
  { key: 'long', label: '长期' }
]

/** 展示倍率：原始斜率 ×1000 后按 % 显示；过滤阈值也用该口径 */
export const SLOPE_DISPLAY_SCALE = 1000

function emptyRange() {
  return { a: '', b: '', c: '' }
}

export function defaultRangeConfig() {
  return {
    short: { a: '5', b: '10', c: '15' },
    mid: { a: '5', b: '10', c: '15' },
    long: { a: '5', b: '10', c: '15' },
    // 展示口径：原始 0.01 → 10（×1000%）
    slopeMin: '10',
    slopeUnit: 'x1000pct',
    /** 距年高涨幅上限（%）：勾选过滤时保留 ≤ 该值 */
    distToYearHighMax: '15',
    /** 盘前初筛综合分下限（竞价TOP变体，默认 45） */
    preScreenScoreMin: '45',
    /** 流通市值上限（亿）：勾选过滤市值时保留 ≤ 该值 */
    marketCapMax: '70',
    /** 首板价上限（元）：勾选过滤首板价时剔除大于该值 */
    firstBoardPriceMax: '20'
  }
}

export function cloneRangeConfig(src) {
  const out = {}
  RANGE_FIELDS.forEach((f) => {
    const r = (src && src[f.key]) || emptyRange()
    if (r.a == null && (r.low != null || r.high != null)) {
      out[f.key] = {
        a: r.low != null ? String(r.low) : '',
        b: r.high != null ? String(r.high) : '',
        c: ''
      }
      return
    }
    out[f.key] = {
      a: r.a != null ? String(r.a) : '',
      b: r.b != null ? String(r.b) : '',
      c: r.c != null ? String(r.c) : ''
    }
  })
  const slopeRaw = src && src.slopeMin != null ? String(src.slopeMin) : '10'
  const n = Number(String(slopeRaw).trim())
  if (src && src.slopeUnit === 'x1000pct') {
    out.slopeMin = String(slopeRaw).trim() === '' || !Number.isFinite(n) ? '10' : String(n)
  } else if (Number.isFinite(n)) {
    // 兼容旧版原始斜率阈值（如 0.01）
    out.slopeMin = String(n * SLOPE_DISPLAY_SCALE)
  } else {
    out.slopeMin = '10'
  }
  out.slopeUnit = 'x1000pct'
  const distRaw = src && src.distToYearHighMax != null ? String(src.distToYearHighMax) : '15'
  const distN = Number(String(distRaw).trim())
  out.distToYearHighMax = String(distRaw).trim() === '' || !Number.isFinite(distN) ? '15' : String(distN)
  const psRaw = src && src.preScreenScoreMin != null ? String(src.preScreenScoreMin) : '45'
  const psN = Number(String(psRaw).trim())
  out.preScreenScoreMin = String(psRaw).trim() === '' || !Number.isFinite(psN) ? '45' : String(psN)
  const capRaw = src && src.marketCapMax != null ? String(src.marketCapMax) : '70'
  const capN = Number(String(capRaw).trim())
  out.marketCapMax = String(capRaw).trim() === '' || !Number.isFinite(capN) ? '70' : String(capN)
  const fbRaw = src && src.firstBoardPriceMax != null ? String(src.firstBoardPriceMax) : '20'
  const fbN = Number(String(fbRaw).trim())
  out.firstBoardPriceMax = String(fbRaw).trim() === '' || !Number.isFinite(fbN) ? '20' : String(fbN)
  return out
}

export function parsePct(raw) {
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(String(raw).trim())
  return Number.isFinite(n) ? n : null
}

export function cutpointsToOptions(range) {
  const aPct = parsePct(range && range.a)
  const bPct = parsePct(range && range.b)
  const cPct = parsePct(range && range.c)
  if (aPct == null || bPct == null || cPct == null) return []
  const a = aPct / 100
  const b = bPct / 100
  const c = cPct / 100
  return [
    { value: `lt_${aPct}`, label: `<${aPct}%`, min: null, max: a },
    { value: `bt_${aPct}_${bPct}`, label: `${aPct}%-${bPct}%`, min: a, max: b },
    { value: `bt_${bPct}_${cPct}`, label: `${bPct}%-${cPct}%`, min: b, max: c }
  ]
}

export function loadRangeConfig() {
  try {
    const raw = localStorage.getItem(FILTER_CONFIG_KEY)
    if (!raw) return defaultRangeConfig()
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return defaultRangeConfig()
    return cloneRangeConfig(parsed)
  } catch (_) {
    return defaultRangeConfig()
  }
}

export function saveRangeConfig(cfg) {
  try {
    localStorage.setItem(FILTER_CONFIG_KEY, JSON.stringify(cfg))
  } catch (_) {
    /* ignore */
  }
}

export function passesChipBands(value, selectedBands, options) {
  const bands = Array.isArray(selectedBands) ? selectedBands.filter(Boolean) : []
  if (!bands.length) return true
  const v = Number(value)
  if (!Number.isFinite(v)) return true
  for (const key of bands) {
    const opt = (options || []).find((o) => o.value === key)
    if (!opt) continue
    const geMin = opt.min == null ? true : v >= opt.min
    const ltMax = opt.max == null ? true : v < opt.max
    if (geMin && ltMax) return true
  }
  return false
}

/** 配置里的斜率阈值（×1000% 展示口径）→ 原始斜率 */
export function slopeThresholdRaw(slopeMinDisplay) {
  const n = Number(slopeMinDisplay)
  if (!Number.isFinite(n)) return 10 / SLOPE_DISPLAY_SCALE
  return n / SLOPE_DISPLAY_SCALE
}

export function formatSlopeX1000Pct(v) {
  if (v == null || v === '') return '-'
  const n = Number(v)
  if (!Number.isFinite(n)) return '-'
  return `${(n * SLOPE_DISPLAY_SCALE).toFixed(2)}%`
}

/** 中期筹码四档键：C0 / C1 / C2(13–20%) / none */
export const MID_CHIP_TIER_KEYS = ['C0', 'C1', 'C2', 'none']

/** 筹码五档键：C0~C3 + none（≥20% 或无有效值）— 短/长期等仍用五档 */
export const CHIP_TIER_KEYS = ['C0', 'C1', 'C2', 'C3', 'none']

const CHIP_TIER_TOP_PCT = 20
const MID_CHIP_C2_MIN_PCT = 13
const MID_CHIP_C2_MAX_PCT = 20

/**
 * 中期筹码四档：C0 < a | C1 a~b | C2 13~20%(含20) | none 其余(含>20%、无值、原C3区间)
 */
export function getMidChipTier(value, range) {
  const v = Number(value)
  const aPct = parsePct(range && range.a) ?? 5
  const bPct = parsePct(range && range.b) ?? 10
  if (!Number.isFinite(v)) return 'none'
  const a = aPct / 100
  const b = bPct / 100
  const c2Min = MID_CHIP_C2_MIN_PCT / 100
  const c2Max = MID_CHIP_C2_MAX_PCT / 100
  if (v < a) return 'C0'
  if (v < b) return 'C1'
  if (v >= c2Min && v <= c2Max) return 'C2'
  return 'none'
}

export function midChipTierButtonDefs(range) {
  const aPct = parsePct(range && range.a) ?? 5
  const bPct = parsePct(range && range.b) ?? 10
  return [
    { key: 'C0', label: `C0 <${aPct}%` },
    { key: 'C1', label: `C1 ${aPct}-${bPct}%` },
    { key: 'C2', label: `C2 ${MID_CHIP_C2_MIN_PCT}-${MID_CHIP_C2_MAX_PCT}%` },
    { key: 'none', label: `无档 >${MID_CHIP_C2_MAX_PCT}%` }
  ]
}

/** 中期筹码多选 OR */
export function passesMidChipTierFilter(value, selectedTiers, range) {
  const tiers = Array.isArray(selectedTiers) ? selectedTiers.filter(Boolean) : []
  if (!tiers.length) return true
  return tiers.includes(getMidChipTier(value, range))
}

/**
 * C0 < a% | C1 a~b | C2 b~c | C3 c~20% | none ≥20% 或无值
 */
export function getChipTier(value, range) {
  const v = Number(value)
  const aPct = parsePct(range && range.a) ?? 5
  const bPct = parsePct(range && range.b) ?? 10
  const cPct = parsePct(range && range.c) ?? 15
  if (!Number.isFinite(v)) return 'none'
  const a = aPct / 100
  const b = bPct / 100
  const c = cPct / 100
  const top = CHIP_TIER_TOP_PCT / 100
  if (v < a) return 'C0'
  if (v < b) return 'C1'
  if (v < c) return 'C2'
  if (v < top) return 'C3'
  return 'none'
}

export function chipTierButtonDefs(range) {
  const aPct = parsePct(range && range.a) ?? 5
  const bPct = parsePct(range && range.b) ?? 10
  const cPct = parsePct(range && range.c) ?? 15
  return [
    { key: 'C0', label: `C0 <${aPct}%` },
    { key: 'C1', label: `C1 ${aPct}-${bPct}%` },
    { key: 'C2', label: `C2 ${bPct}-${cPct}%` },
    { key: 'C3', label: `C3 ${cPct}-${CHIP_TIER_TOP_PCT}%` },
    { key: 'none', label: `无档 ≥${CHIP_TIER_TOP_PCT}%` }
  ]
}

/** 多选档位 OR：未选则不过滤 */
export function passesChipTierFilter(value, selectedTiers, range) {
  const tiers = Array.isArray(selectedTiers) ? selectedTiers.filter(Boolean) : []
  if (!tiers.length) return true
  return tiers.includes(getChipTier(value, range))
}

/**
 * 应用与历史首板一致的筛选（不含类型）
 */
export function applyHfbStyleFilters(rows, state) {
  let list = Array.isArray(rows) ? rows : []
  if (state.filterBaoVol) {
    list = list.filter((row) => row.bao2 === true || row.bao3 === true)
  }
  if (state.filterTouchBoard) {
    list = list.filter((row) => row.touchBoardNext === true)
  }
  if (state.filterSlope) {
    const thr = slopeThresholdRaw(state.slopeMinValue)
    list = list.filter((row) => {
      const v = Number(row.chipSlope)
      if (!Number.isFinite(v)) return true
      return v <= thr
    })
  }
  if (state.filterMarketCap70) {
    const capMax = Number(state.marketCapMaxValue)
    const thr = Number.isFinite(capMax) ? capMax : 70
    list = list.filter((row) => {
      const v = Number(row.freeFloatMarketCap)
      return Number.isFinite(v) && v <= thr
    })
  }
  if (state.filterMa250) {
    // 与补涨分析统一：首板价×1.1×1.1 > maMax（max MA10…MA250）
    list = list.filter((row) =>
      passesMa250Filter({
        firstBoardPrice: row.firstBoardPrice,
        maMax: row.maMax,
        ma10: row.ma10,
        ma20: row.ma20,
        ma30: row.ma30,
        ma40: row.ma40,
        ma60: row.ma60,
        ma110: row.ma110,
        ma120: row.ma120,
        ma160: row.ma160,
        ma210: row.ma210,
        ma250: row.ma250
      })
    )
  }
  if (state.filterAboveAllMa) {
    list = list.filter((row) => Number(row.aboveAllMa) === 1)
  }
  if (state.filterDistToYearHigh) {
    const maxPct = Number(state.distToYearHighMaxValue)
    const thr = Number.isFinite(maxPct) ? maxPct : 15
    list = list.filter((row) => {
      const v = Number(row.distToYearHighPct)
      if (!Number.isFinite(v)) return false
      return v <= thr
    })
  }
  if (state.filterPreScreenScore) {
    const minScore = Number(state.preScreenScoreMinValue)
    const thr = Number.isFinite(minScore) ? minScore : 45
    list = list.filter((row) => {
      const v = Number(row.preScreenScore)
      if (!Number.isFinite(v)) return false
      return v >= thr
    })
  }
  list = list.filter((row) => {
    if (!passesChipBands(row.shortChip, state.filterShortChipBands, state.shortChipOptions)) return false
    if (!passesChipBands(row.midChip, state.filterMidChipBands, state.midChipOptions)) return false
    if (!passesChipBands(row.longChip, state.filterLongChipBands, state.longChipOptions)) return false
    return true
  })
  return list
}
